import { createFileRoute, Outlet, type ToOptions } from '@tanstack/react-router'
import { Calendar, CalendarCheck2, LayoutGrid, Users, type LucideIcon } from 'lucide-react'
import { useEffect } from 'react'
import z from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { HrAttendanceSelector } from '~/components/routes/hr/planning/hr-attendance-selector'
import { HrStoreSelector } from '~/components/routes/hr/planning/hr-store-selector'
import { HrDashboardSelector } from '~/components/routes/hr/planning/dashboard/hr-dashboard-selector'
import {
  HR_DATE_PRESETS,
  hrDashboardPeriodLabel,
  resolveHrDashboardRange,
} from '~/components/routes/hr/planning/dashboard/hr-dashboard-range'
import { Title } from '~/components/new/app-ui/title'
import { resolveAttendanceRange } from '~/utils/dates/attendance-range'
import { resolveStoreSet } from '~/utils/hr/resolveStoreSet'
import { PlanningAbsenceCalendar } from '~/components/routes/hr/planning/absence-calendar'
import { PlanningAttendance } from '~/components/routes/hr/planning/attendance'
import { PlanningCollaborator } from '~/components/routes/hr/planning/collaborators'
import { PlanningDashboard } from '~/components/routes/hr/planning/dashboard'
import { PlanningCalendar } from '~/components/routes/hr/planning/planning'
import { ExportAttendanceButton } from '~/components/routes/hr/planning/attendance/export-attendance-button'
import { ExportPlanningButton } from '~/components/routes/hr/planning/planning/export-planning-button'
import { PlanningSettingsDialog } from '~/components/routes/hr/planning/planning/planning-settings'
import { SelectionPeriodBadge } from '~/components/routes/network-dashboard/selection-period-badge'
import { useSidebar } from '~/components/ui/sidebar'
import { HR_PAGE_READ, HR_TABS } from '~/components/routes/hr/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { useAuthorizedTabs, useFirstAuthorizedTab } from '~/utils/rbacTabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'

// Le parsing de route se fait au chargement du module : la liste doit inclure 'dashboard'
// pour que l'URL reste valide. L'affichage de l'onglet dans la topbar est piloté par le
// feature flag 'dashboard-rh' à l'intérieur du composant (voir RouteComponent).
const tabValues = [
  'dashboard',
  'calendar',
  'collaborators',
  'attendance',
  'absence-calendar',
] as const
type TabValue = (typeof tabValues)[number]

// ISO 'YYYY-MM-DD' pour les params de plage du dashboard.
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const Route = createFileRoute('/_authenticated/hr/planning/$tab')({
  staticData: {
    permission: HR_PAGE_READ,
  },
  component: RouteComponent,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
  validateSearch: z.object({
    // Global search params
    storeId: z.number().optional(),
    // Multi-sélection de l'onglet Tableau de bord — état séparé de storeId (aucune synchro).
    storeIds: z.array(z.number()).optional(),
    // Onglets Pointage / Planning : set multi-boutique dédié à l'EXPORT (l'affichage reste mono
    // via storeId). Param distinct de `storeIds` (dashboard) pour éviter toute contamination croisée.
    exportStoreIds: z.array(z.number()).optional(),

    // Dashboard : plage de dates libre + préset (réutilise le sélecteur du Network Dashboard).
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    datePreset: z.enum(HR_DATE_PRESETS).optional(),

    // Planning specific search params
    weekNumber: z.number().optional(),
    year: z.number().optional(),

    // Pointage : plage de dates pilotant à la fois le tableau et l'export (ISO 'YYYY-MM-DD').
    dateStart: z.string().optional(),
    dateEnd: z.string().optional(),
  }),
})
withRoutePermissions(Route)

const tabOptions = {
  dashboard: {
    label: 'Tableau de bord',
    icon: LayoutGrid,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'dashboard' } },
    component: <PlanningDashboard />,
  },
  calendar: {
    label: 'Planning',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'calendar' } },
    component: <PlanningCalendar />,
  },
  collaborators: {
    label: 'Personnel',
    icon: Users,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'collaborators' } },
    component: <PlanningCollaborator />,
  },
  attendance: {
    label: 'Pointage',
    icon: CalendarCheck2,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'attendance' } },
    component: <PlanningAttendance />,
  },
  'absence-calendar': {
    label: 'Calendrier Absences',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'absence-calendar' } },
    component: <PlanningAbsenceCalendar />,
  },
} as const satisfies Record<
  TabValue,
  { toOptions: ToOptions; component: React.ReactNode; label: string; icon: LucideIcon }
>

function RouteComponent() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { storeId, exportStoreIds, dateStart, dateEnd } = search
  const { tab } = Route.useParams()

  const dashboardRhDisplayed = useFeatureFlag('dashboard-rh')
  const planningMultiStore = useFeatureFlag('hr-export-planning-multi-store')
  const attendanceMultiStore = useFeatureFlag('hr-export-attendance-multi-store')

  const authorizedTabs = useAuthorizedTabs(HR_TABS)
  const firstAuthorizedTab = useFirstAuthorizedTab(authorizedTabs)

  // Onglet Tableau de bord masqué tant que le flag est off ; on retire aussi son entrée
  // de la topbar. Un accès direct à l'URL /hr/planning/dashboard est redirigé vers Planning.
  const visibleTabs = authorizedTabs.filter(
    (item) => item.value !== 'dashboard' || dashboardRhDisplayed,
  )

  // Un onglet interdit reste affiché (désactivé + tooltip), mais on n'en montre jamais le
  // contenu : on retombe sur le premier onglet accessible.
  const activeTab = authorizedTabs.find((item) => item.value === tab)?.authorized
    ? tab
    : (firstAuthorizedTab ?? tab)

  useEffect(() => {
    if (tab === 'dashboard' && !dashboardRhDisplayed) {
      navigate({ ...tabOptions.calendar.toOptions, search: (prev) => ({ ...prev }) })
    }
  }, [tab, dashboardRhDisplayed, navigate])

  // Plage pointage partagée (tableau + export) ; défaut = semaine courante quand l'URL est vide.
  const attendanceRange = resolveAttendanceRange(dateStart, dateEnd)

  // Multi-boutique (attendance / calendar) : la page affiche `storeId` (= tête) mais l'export porte
  // sur tout le set choisi (`exportStoreIds`), avec fallback sur la boutique unique. `[]` ⇒ undefined
  // pour que les boutons d'export interprètent « aucun filtre » = toutes les boutiques.
  const resolvedExportSet = resolveStoreSet(storeId, exportStoreIds)
  const exportStoreSet = resolvedExportSet.length > 0 ? resolvedExportSet : undefined

  const singleStoreSet = storeId === undefined ? undefined : [storeId]
  const attendanceExportSet = attendanceMultiStore ? exportStoreSet : singleStoreSet
  const planningExportSet = planningMultiStore ? exportStoreSet : undefined

  // The HR dashboard mockup (Figma 7007:11548) is laid out for a 1440px viewport with the
  // sidebar collapsed to its icon rail — collapse it while in this section, restore on leave.
  // Mount/unmount only: re-running on sidebar state changes would re-collapse the sidebar
  // whenever the user expands it manually.
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()
  useEffect(() => {
    const wasOpen = sidebarOpen
    setSidebarOpen(false)
    return () => setSidebarOpen(wasOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const TabComponent = tabOptions[activeTab].component

  const { startIso, endIso } = resolveHrDashboardRange(search)

  return (
    <>
      {/* Header + AppContainer alignés sur le Dashboard Réseau (py-4, titre 32px, mb-4, et paddings
          AppContainer par défaut p-4/gap-4 = mêmes marges au bord des onglets que Réseau, à la
          demande client). */}
      <AppHeader className="px-5 py-4 mb-4">
        <div className="flex items-center gap-2">
          <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
            RH & Planning
          </Title>
          {activeTab === 'dashboard' && (
            <SelectionPeriodBadge
              startDate={startIso}
              endDate={endIso}
              label={hrDashboardPeriodLabel(startIso, endIso)}
            />
          )}
        </div>
      </AppHeader>

      <AppContainer>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AppTabs
            tabs={visibleTabs.map((item) => ({
              label: tabOptions[item.value].label,
              icon: tabOptions[item.value].icon,
              value: item.value,
              disabled: !item.authorized,
              tooltip: item.tooltip,
            }))}
            value={activeTab}
            onTabChange={(v) =>
              navigate({
                ...tabOptions[v].toOptions,
                search: (prev) => ({ ...prev }),
              })
            }
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {activeTab === 'dashboard' && <HrDashboardSelector />}
            {activeTab === 'attendance' && <HrAttendanceSelector />}
            {(activeTab === 'calendar' ||
              activeTab === 'collaborators' ||
              activeTab === 'absence-calendar') && (
              <HrStoreSelector
                asCta={activeTab !== 'collaborators'}
                multi={activeTab === 'calendar' && planningMultiStore}
              />
            )}
            {activeTab === 'calendar' && <ExportPlanningButton storeIds={planningExportSet} />}
            {activeTab === 'attendance' && (
              <ExportAttendanceButton
                storeIds={attendanceExportSet}
                dateStart={attendanceRange.dateStart}
                dateEnd={attendanceRange.dateEnd}
              />
            )}
            {activeTab === 'calendar' && <PlanningSettingsDialog />}
          </div>
        </div>

        {TabComponent}
      </AppContainer>

      {/* Panneau latéral d'édition du collaborateur focus (route imbriquée /hr/planning/$tab/$collaboratorId) */}
      <Outlet />
    </>
  )
}
